import { z } from "zod";
import {
  openDB,
  type DBSchema,
  type IDBPDatabase
} from "idb";

import type {
  AttemptResult,
  ChildProfile,
  Domain,
  DomainMastery,
  ProgressRepository,
  RewardState
} from "@/domain";

import {
  MAX_CHILD_PROFILES,
  PROFILE_EXPORT_SCHEMA_VERSION,
  ProfileLimitError,
  ProfileNotFoundError,
  attemptResultSchema,
  createEmptyRewards,
  decodeProfileExport,
  domainMasterySchema,
  encodeProfileExport,
  parseAttemptResult,
  parseChildProfile,
  parseMastery,
  parseRewardState,
  rewardStateSchema,
  sortAttempts,
  sortMastery,
  sortProfiles
} from "./repository-codec";

export const PROGRESS_DATABASE_VERSION = 1;
export const DEFAULT_PROGRESS_DATABASE_NAME = "little-launch-island-progress";

const PROFILE_STORE = "profiles";
const ATTEMPT_STORE = "attempts";
const MASTERY_STORE = "mastery";
const REWARD_STORE = "rewards";
const PROFILE_INDEX = "by-profile";
const ALL_STORE_NAMES = [
  PROFILE_STORE,
  ATTEMPT_STORE,
  MASTERY_STORE,
  REWARD_STORE
] as const;

const storedMasterySchema = domainMasterySchema.extend({
  profileId: z.string().min(1)
});

const storedRewardSchema = rewardStateSchema.extend({
  profileId: z.string().min(1)
});

type StoredMastery = z.infer<typeof storedMasterySchema>;
type StoredReward = z.infer<typeof storedRewardSchema>;

interface ProgressDatabaseSchema extends DBSchema {
  profiles: {
    key: string;
    value: ChildProfile;
  };
  attempts: {
    key: [string, string];
    value: AttemptResult;
    indexes: { "by-profile": string };
  };
  mastery: {
    key: [string, Domain];
    value: StoredMastery;
    indexes: { "by-profile": string };
  };
  rewards: {
    key: string;
    value: StoredReward;
  };
}

export interface IndexedDbProgressRepositoryOptions {
  databaseName?: string;
}

async function openProgressDatabase(
  databaseName: string
): Promise<IDBPDatabase<ProgressDatabaseSchema>> {
  const connection = await openDB<ProgressDatabaseSchema>(
    databaseName,
    PROGRESS_DATABASE_VERSION,
    {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          database.createObjectStore(PROFILE_STORE, { keyPath: "id" });

          const attempts = database.createObjectStore(ATTEMPT_STORE, {
            keyPath: ["profileId", "id"]
          });
          attempts.createIndex(PROFILE_INDEX, "profileId", { unique: false });

          const mastery = database.createObjectStore(MASTERY_STORE, {
            keyPath: ["profileId", "domain"]
          });
          mastery.createIndex(PROFILE_INDEX, "profileId", { unique: false });

          database.createObjectStore(REWARD_STORE, { keyPath: "profileId" });
        }
      },
      blocking() {
        connection.close();
      }
    }
  );

  return connection;
}

async function completeWrite(
  done: Promise<void>,
  operations: Promise<unknown>[]
): Promise<void> {
  try {
    await Promise.all(operations);
    await done;
  } catch (error) {
    await done.catch(() => undefined);
    throw error;
  }
}

function assertProfileMatch(expectedProfileId: string, actualProfileId: string): void {
  if (expectedProfileId !== actualProfileId) {
    throw new Error("Stored progress belongs to a different profile.");
  }
}

function parseAttemptsForProfile(
  records: readonly unknown[],
  profileId: string
): AttemptResult[] {
  const attempts = records.map((record) => parseAttemptResult(record));
  attempts.forEach((attempt) => {
    assertProfileMatch(profileId, attempt.profileId);
  });
  return sortAttempts(attempts);
}

function parseMasteryForProfile(
  records: readonly unknown[],
  profileId: string
): DomainMastery[] {
  const mastery = records.map((record) => {
    const stored = storedMasterySchema.parse(record);
    assertProfileMatch(profileId, stored.profileId);
    return domainMasterySchema.parse({
      domain: stored.domain,
      score: stored.score,
      recommendedDifficulty: stored.recommendedDifficulty,
      updatedAt: stored.updatedAt
    });
  });
  return sortMastery(parseMastery(mastery));
}

function parseRewardsForProfile(record: unknown, profileId: string): RewardState {
  if (record === undefined) {
    return createEmptyRewards();
  }

  const stored = storedRewardSchema.parse(record);
  assertProfileMatch(profileId, stored.profileId);
  return parseRewardState({
    stamps: stored.stamps,
    unlockedDecorationIds: stored.unlockedDecorationIds
  });
}

function toStoredMastery(
  profileId: string,
  mastery: DomainMastery
): StoredMastery {
  return storedMasterySchema.parse({ profileId, ...mastery });
}

function toStoredRewards(profileId: string, rewards: RewardState): StoredReward {
  return storedRewardSchema.parse({ profileId, ...rewards });
}

export class IndexedDbProgressRepository implements ProgressRepository {
  private readonly databaseName: string;
  private databasePromise?: Promise<IDBPDatabase<ProgressDatabaseSchema>>;

  constructor(options: IndexedDbProgressRepositoryOptions = {}) {
    this.databaseName = options.databaseName ?? DEFAULT_PROGRESS_DATABASE_NAME;
  }

  async initialize(): Promise<void> {
    await this.getDatabase();
  }

  async close(): Promise<void> {
    const databasePromise = this.databasePromise;
    this.databasePromise = undefined;

    if (databasePromise !== undefined) {
      const database = await databasePromise;
      database.close();
    }
  }

  async listProfiles(): Promise<ChildProfile[]> {
    const database = await this.getDatabase();
    const records = await database.getAll(PROFILE_STORE);
    return sortProfiles(records.map((record) => parseChildProfile(record)));
  }

  async getProfile(profileId: string): Promise<ChildProfile | undefined> {
    const database = await this.getDatabase();
    const record = await database.get(PROFILE_STORE, profileId);
    return record === undefined ? undefined : parseChildProfile(record);
  }

  async saveProfile(profile: ChildProfile): Promise<void> {
    const parsedProfile = parseChildProfile(profile);
    const database = await this.getDatabase();
    const transaction = database.transaction(PROFILE_STORE, "readwrite");
    const store = transaction.store;
    const [existing, profileCount] = await Promise.all([
      store.get(parsedProfile.id),
      store.count()
    ]);

    if (existing === undefined && profileCount >= MAX_CHILD_PROFILES) {
      await transaction.done;
      throw new ProfileLimitError();
    }

    await completeWrite(transaction.done, [store.put(parsedProfile)]);
  }

  async deleteProfile(profileId: string): Promise<void> {
    const database = await this.getDatabase();
    const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
    const profileStore = transaction.objectStore(PROFILE_STORE);
    const attemptStore = transaction.objectStore(ATTEMPT_STORE);
    const masteryStore = transaction.objectStore(MASTERY_STORE);
    const rewardStore = transaction.objectStore(REWARD_STORE);
    const [attemptKeys, masteryKeys] = await Promise.all([
      attemptStore.index(PROFILE_INDEX).getAllKeys(profileId),
      masteryStore.index(PROFILE_INDEX).getAllKeys(profileId)
    ]);
    const operations: Promise<unknown>[] = [
      profileStore.delete(profileId),
      rewardStore.delete(profileId),
      ...attemptKeys.map((key) => attemptStore.delete(key)),
      ...masteryKeys.map((key) => masteryStore.delete(key))
    ];

    await completeWrite(transaction.done, operations);
  }

  async listAttempts(profileId: string): Promise<AttemptResult[]> {
    const database = await this.getDatabase();
    const records = await database.getAllFromIndex(
      ATTEMPT_STORE,
      PROFILE_INDEX,
      profileId
    );
    return parseAttemptsForProfile(records, profileId);
  }

  async saveAttempt(result: AttemptResult): Promise<void> {
    const attempt = attemptResultSchema.parse(result);
    const database = await this.getDatabase();
    const transaction = database.transaction(
      [PROFILE_STORE, ATTEMPT_STORE],
      "readwrite"
    );
    const profile = await transaction.objectStore(PROFILE_STORE).get(attempt.profileId);

    if (profile === undefined) {
      await transaction.done;
      throw new ProfileNotFoundError(attempt.profileId);
    }

    await completeWrite(transaction.done, [
      transaction.objectStore(ATTEMPT_STORE).put(attempt)
    ]);
  }

  async getMastery(profileId: string): Promise<DomainMastery[]> {
    const database = await this.getDatabase();
    const records = await database.getAllFromIndex(
      MASTERY_STORE,
      PROFILE_INDEX,
      profileId
    );
    return parseMasteryForProfile(records, profileId);
  }

  async saveMastery(
    profileId: string,
    mastery: DomainMastery[]
  ): Promise<void> {
    const parsedMastery = parseMastery(mastery);
    const database = await this.getDatabase();
    const transaction = database.transaction(
      [PROFILE_STORE, MASTERY_STORE],
      "readwrite"
    );
    const profileStore = transaction.objectStore(PROFILE_STORE);
    const masteryStore = transaction.objectStore(MASTERY_STORE);
    const [profile, oldKeys] = await Promise.all([
      profileStore.get(profileId),
      masteryStore.index(PROFILE_INDEX).getAllKeys(profileId)
    ]);

    if (profile === undefined) {
      await transaction.done;
      throw new ProfileNotFoundError(profileId);
    }

    const operations: Promise<unknown>[] = [
      ...oldKeys.map((key) => masteryStore.delete(key)),
      ...parsedMastery.map((entry) =>
        masteryStore.put(toStoredMastery(profileId, entry))
      )
    ];
    await completeWrite(transaction.done, operations);
  }

  async getRewards(profileId: string): Promise<RewardState> {
    const database = await this.getDatabase();
    const record = await database.get(REWARD_STORE, profileId);
    return parseRewardsForProfile(record, profileId);
  }

  async saveRewards(profileId: string, rewards: RewardState): Promise<void> {
    const parsedRewards = parseRewardState(rewards);
    const database = await this.getDatabase();
    const transaction = database.transaction(
      [PROFILE_STORE, REWARD_STORE],
      "readwrite"
    );
    const profile = await transaction.objectStore(PROFILE_STORE).get(profileId);

    if (profile === undefined) {
      await transaction.done;
      throw new ProfileNotFoundError(profileId);
    }

    await completeWrite(transaction.done, [
      transaction
        .objectStore(REWARD_STORE)
        .put(toStoredRewards(profileId, parsedRewards))
    ]);
  }

  async exportProfile(profileId: string): Promise<string> {
    const database = await this.getDatabase();
    const transaction = database.transaction(ALL_STORE_NAMES, "readonly");
    const [profileRecord, attemptRecords, masteryRecords, rewardRecord] =
      await Promise.all([
        transaction.objectStore(PROFILE_STORE).get(profileId),
        transaction
          .objectStore(ATTEMPT_STORE)
          .index(PROFILE_INDEX)
          .getAll(profileId),
        transaction
          .objectStore(MASTERY_STORE)
          .index(PROFILE_INDEX)
          .getAll(profileId),
        transaction.objectStore(REWARD_STORE).get(profileId)
      ]);
    await transaction.done;

    if (profileRecord === undefined) {
      throw new ProfileNotFoundError(profileId);
    }

    return encodeProfileExport({
      schemaVersion: PROFILE_EXPORT_SCHEMA_VERSION,
      profile: parseChildProfile(profileRecord),
      attempts: parseAttemptsForProfile(attemptRecords, profileId),
      mastery: parseMasteryForProfile(masteryRecords, profileId),
      rewards: parseRewardsForProfile(rewardRecord, profileId)
    });
  }

  async importProfile(serialized: string): Promise<ChildProfile> {
    const data = decodeProfileExport(serialized);
    const database = await this.getDatabase();
    const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
    const profileStore = transaction.objectStore(PROFILE_STORE);
    const attemptStore = transaction.objectStore(ATTEMPT_STORE);
    const masteryStore = transaction.objectStore(MASTERY_STORE);
    const rewardStore = transaction.objectStore(REWARD_STORE);
    const [existing, profileCount, oldAttemptKeys, oldMasteryKeys] =
      await Promise.all([
        profileStore.get(data.profile.id),
        profileStore.count(),
        attemptStore.index(PROFILE_INDEX).getAllKeys(data.profile.id),
        masteryStore.index(PROFILE_INDEX).getAllKeys(data.profile.id)
      ]);

    if (existing === undefined && profileCount >= MAX_CHILD_PROFILES) {
      await transaction.done;
      throw new ProfileLimitError();
    }

    const operations: Promise<unknown>[] = [
      profileStore.put(data.profile),
      rewardStore.put(toStoredRewards(data.profile.id, data.rewards)),
      ...oldAttemptKeys.map((key) => attemptStore.delete(key)),
      ...oldMasteryKeys.map((key) => masteryStore.delete(key)),
      ...data.attempts.map((attempt) => attemptStore.put(attempt)),
      ...data.mastery.map((entry) =>
        masteryStore.put(toStoredMastery(data.profile.id, entry))
      )
    ];
    await completeWrite(transaction.done, operations);

    return parseChildProfile(data.profile);
  }

  async clearAll(): Promise<void> {
    const database = await this.getDatabase();
    const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
    await completeWrite(transaction.done, [
      transaction.objectStore(PROFILE_STORE).clear(),
      transaction.objectStore(ATTEMPT_STORE).clear(),
      transaction.objectStore(MASTERY_STORE).clear(),
      transaction.objectStore(REWARD_STORE).clear()
    ]);
  }

  private getDatabase(): Promise<IDBPDatabase<ProgressDatabaseSchema>> {
    this.databasePromise ??= openProgressDatabase(this.databaseName);
    return this.databasePromise;
  }
}
